import React, { useState } from 'react';
import cn from 'classnames';
import PropTypes from 'prop-types';
import { ExclamationCircleIcon } from '@heroicons/react/outline';
import { HiOutlineEye, HiOutlineEyeOff } from 'react-icons/hi';
import { useInputId } from './hooks';

function FormPassword({
  id: defaultId,
  label,
  name,
  onChange,
  value,
  error,
  required,
  ...props
}) {
  const [id] = useInputId(defaultId);
  const [show, setShow] = useState(false);

  const handleChange = ({ target }) => {
    onChange((state) => ({
      ...state,
      [name]: target.value,
    }));
  };

  const isError = !!error;
  return (
    <div
      className={cn(
        'bg-white relative border rounded-md px-3 py-2 shadow-sm focus-within:ring-1',
        isError
          ? 'border-red-300 focus-within:ring-red-500 focus-within:border-red-500'
          : 'border-gray-300 focus-within:ring-primary-600 focus-within:border-primary-600'
      )}
    >
      <label htmlFor={id} className="block text-xs font-medium text-gray-900">
        <span>{label}</span>
        {!required && <span className="ml-1 font-light">(Optional)</span>}
      </label>
      <input
        id={id}
        type={show ? 'text' : 'password'}
        required={required}
        name="name"
        className={cn(
          'block w-full border-0 p-0 text-gray-900 placeholder-gray-500 focus:ring-0 sm:text-sm',
          'focus:outline-none pr-10'
        )}
        onChange={handleChange}
        value={value || ''}
        {...props}
      />

      <div
        className="absolute inset-y-0 right-0 pr-3 flex items-center"
        title={error || ''}
      >
        {error ? (
          <ExclamationCircleIcon
            className="h-5 w-5 text-red-500"
            aria-hidden="true"
          />
        ) : (
          <button type="button" onClick={() => setShow(!show)}>
            {!show ? (
              <HiOutlineEyeOff
                className="h-5 w-5 text-gray-400"
                aria-hidden="true"
              />
            ) : (
              <HiOutlineEye
                className="h-5 w-5 text-gray-400"
                aria-hidden="true"
              />
            )}
          </button>
        )}
      </div>
    </div>
  );
}

FormPassword.defaultProps = {
  id: '',
  error: null,
  required: false,
};

FormPassword.propTypes = {
  id: PropTypes.string,
  required: PropTypes.bool,
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  error: PropTypes.oneOfType([PropTypes.instanceOf(Object), PropTypes.string]),
  onChange: PropTypes.func.isRequired,
};

export default React.memo(FormPassword);
